import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Table,
  TableLazyLoadEvent,
  TableModule,
  TableRowReorderEvent as PrimengTableRowReorderEvent,
} from 'primeng/table';
import {
  catchError,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs';
import { SortMeta } from 'primeng/api';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { TableHeaderRowComponent } from '../table-header-row/table-header-row.component';
import { TableFilterRowComponent } from '../table-filter-row/table-filter-row.component';
import { TableBodyRowComponent } from '../table-body-row/table-body-row.component';
import { BodyRowSelectionDisabledPipe } from '../../pipes/body-row-selection-disabled.pipe';
import { BodyRowCssClassPipe } from '../../pipes/body-row-css-class.pipe';
import { TableEmptyMessageRowComponent } from '../table-empty-message-row/table-empty-message-row.component';
import { TablePaginatorComponent } from '../table-paginator/table-paginator.component';
import { TableHeaderVisibilityPipe } from '../../pipes/table-header-visibility.pipe';
import {
  TableCollectionAction,
  TableColumn,
  TableConfig,
  TableCustomCellTemplateContext,
  TableCustomCollectionActionsTemplateContext,
  TableDataPayload,
  TableFilterConfig,
  TableItemAction,
  TableLazyLoad,
  TableRowsSelection,
} from '../../models';
import {
  isArray,
  isNil,
  Nillable,
  once,
  ReplaySubjectOnce,
  valueOrDefault,
} from '@web/utils';
import { BodyRowCssClassFactory } from '../../types/body-row-css-class-factory';
import { BodyRowSelectionDisabledFactory } from '../../types/body-row-selection-disabled-factory';
import { TableDataSource } from '../../providers/table-data.source';
import { TableDataKey } from '../../types/table-data-key';
import { TableService } from '../../services/table.service';
import { TableEventsSourceService } from '../../services/table-events-source.service';
import { TableFilterChangeEvent } from '../../events/table-filter-change.event';
import { TablePageChangeEvent } from '../../events/table-page-change.event';
import { TableRowsSelectionChangeEvent } from '../../events/table-rows-selection-change.event';
import {
  CollectionFilterKey,
  CollectionFilterValue,
  TableFilter,
  TableSort,
} from '@web/common';
import { ActionButton } from '../../../action/models/action-button';
import { TableRowReorderEvent } from '../../events/table-row-reorder.event';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_PAGE_SIZE } from '../../consts/default-page-size';
import { TableTopContentScrollDirective } from '../../directives/table-top-content-scroll.directive';
import { TableCurrentPageRowsSelectionDirective } from '../../directives/table-current-page-rows-selection.directive';

@Component({
  selector: 'app-table',
  imports: [
    BodyRowCssClassPipe,
    BodyRowSelectionDisabledPipe,
    CommonModule,
    TableBodyRowComponent,
    TableEmptyMessageRowComponent,
    TableFilterRowComponent,
    TableHeaderComponent,
    TableHeaderRowComponent,
    TableHeaderVisibilityPipe,
    TableModule,
    TablePaginatorComponent,
    TableTopContentScrollDirective,
    TableCurrentPageRowsSelectionDirective,
  ],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<Item, Filter, ItemAction, CollectionAction, Action>
  implements OnInit, OnChanges
{
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly table = inject(TableService, { optional: true });
  /* prettier-ignore */
  private readonly tableEventsSource = inject<TableEventsSourceService<Filter>>(TableEventsSourceService, { optional: true });

  /* prettier-ignore */
  public readonly config = input.required<TableConfig<Item, Filter, ItemAction, CollectionAction, Action>>();
  /* prettier-ignore */
  public readonly customCellTemplateRef = input<Nillable<TemplateRef<TableCustomCellTemplateContext<Item, Filter>>>>();
  /* prettier-ignore */
  public readonly customCollectionActionsTemplateRef = input<Nillable<TemplateRef<TableCustomCollectionActionsTemplateContext<Item, Filter>>>>();
  /* prettier-ignore */
  public readonly customActionsTemplateRef = input<Nillable<TemplateRef<void>>>();
  public readonly rowReorderEvent = output<TableRowReorderEvent<Item>>();
  public readonly selectionChangeEvent =
    output<TableRowsSelectionChangeEvent<Item>>();
  public readonly refreshed$: Observable<void>;

  protected readonly data = signal<Item[]>([]);
  protected readonly total = signal(0);
  protected readonly first = signal(0);
  protected readonly rows = signal(DEFAULT_PAGE_SIZE);
  protected readonly pending = signal(false);

  protected selection$: Observable<Nillable<TableRowsSelection<Item, Filter>>>;
  protected bodyRowCssClassFn: Nillable<BodyRowCssClassFactory<Item>>;
  protected bodyRowSelectionDisabledFn!: BodyRowSelectionDisabledFactory<Item>;
  protected columns!: TableColumn<Item, Filter>[];
  protected actions!: Nillable<ActionButton<Action>[]>;
  /* prettier-ignore */
  protected collectionActions!: Nillable<TableCollectionAction<Item, Filter, CollectionAction>[]>;
  protected itemActions!: Nillable<TableItemAction<Item, ItemAction>[]>;
  protected filters!: TableFilterConfig<Filter>[];
  protected dataSource!: TableDataSource<Item, Filter>;
  protected isFirstLoaded = false;
  protected dataKey!: TableDataKey<Item>;
  protected filtering!: boolean;
  protected pagination!: boolean;
  protected preserveSelectionOnFilter!: boolean;
  protected reordering!: boolean;
  protected topScrollEnabled!: boolean;
  protected scrollHeight!: string;
  protected responsiveLayout!: 'scroll' | 'stack';

  private readonly component = viewChild.required(Table);
  /* prettier-ignore */
  private readonly tableLazyLoadTrigger$ = new ReplaySubjectOnce<TableLazyLoad<Filter>>();
  private readonly dataLoadedSource$ = new Subject<Item[]>();
  /* prettier-ignore */
  private readonly selectionSource$ = new ReplaySubjectOnce<TableRowsSelection<Item, Filter>>();
  private readonly refreshedSource$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  private haveSelectedRowsSource$: Observable<boolean>;

  public constructor() {
    this.haveSelectedRowsSource$ = this.haveSelectedRowsSource();
    this.selection$ = this.selectionSource();
    this.refreshed$ = this.refreshedSource();
  }

  public get dataLoaded$(): Observable<Item[]> {
    return this.dataLoadedSource$.asObservable();
  }

  public ngOnInit(): void {
    this.listenToTableLazyLoad();
    this.listenToTableRefresh();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { config } = changes;

    if (config) {
      this.extractConfiguration();
    }
  }

  public handleLazeLoad(lazyLoadEvent: TableLazyLoadEvent): void {
    this.triggerTableLazyLoad(this.createLazyLoadMetadata(lazyLoadEvent));
  }

  public handleFilterChanges(event: TableFilterChangeEvent<Filter>): void {
    const { value, key } = event;

    this.filterBy(key, value);
  }

  public handlePageChange(event: TablePageChangeEvent): void {
    this.component().onPageChange(event);
  }

  public handleClearSortEvent(): void {
    this.clearAllSortsAndTriggerTableLazyLoad();
  }

  public handleSelectionChangeEvent(
    event: TableRowsSelectionChangeEvent<Item>,
  ): void {
    this.selectionSource$.next(this.createTableRowsSelection(event));
    this.selectionChangeEvent.emit(event);
  }

  public handeRowReorder(event: PrimengTableRowReorderEvent): void {
    const { dragIndex, dropIndex } = event;

    if (isNil(dragIndex) || isNil(dropIndex)) {
      return;
    }

    const item = this.data()[dragIndex];

    this.rowReorderEvent.emit(
      new TableRowReorderEvent(item, dragIndex, dropIndex),
    );
  }

  protected enabledStackResponsiveLayout(): boolean {
    return this.responsiveLayout === 'stack';
  }

  private extractConfiguration(): void {
    const {
      columns,
      actions,
      collectionActions,
      itemActions,
      filters,
      dataSource,
      bodyRowCssClass,
      bodyRowSelectionDisabledFn,
      dataKey,
      filtering,
      pagination,
      preserveSelectionOnFilter,
      reordering,
      topScrollEnabled,
      scrollHeight,
      responsiveLayout,
    } = this.config();

    this.columns = columns;
    this.actions = actions;
    this.collectionActions = collectionActions;
    this.itemActions = itemActions;
    this.filters = filters;
    this.dataSource = dataSource;
    this.bodyRowCssClassFn = bodyRowCssClass;
    this.bodyRowSelectionDisabledFn = bodyRowSelectionDisabledFn;
    this.dataKey = dataKey;
    this.filtering = filtering;
    this.pagination = pagination;
    this.preserveSelectionOnFilter = preserveSelectionOnFilter;
    this.reordering = reordering;
    this.topScrollEnabled = topScrollEnabled;
    this.scrollHeight = scrollHeight;
    this.responsiveLayout = responsiveLayout;
  }

  private detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  private createLazyLoadMetadata(
    lazyLoadEvent: TableLazyLoadEvent,
  ): TableLazyLoad<Filter> {
    const first = valueOrDefault(lazyLoadEvent.first, 0);
    const sort = this.prepareTableSort(lazyLoadEvent);
    const filter = this.prepareTableFilter(lazyLoadEvent);
    const rows = valueOrDefault(lazyLoadEvent.rows, DEFAULT_PAGE_SIZE);
    const rowsWithoutZero = rows === 0 ? DEFAULT_PAGE_SIZE : rows;

    return new TableLazyLoad<Filter>(first, rowsWithoutZero, sort, filter);
  }

  private createTableRowsSelection(
    event: TableRowsSelectionChangeEvent<Item>,
  ): TableRowsSelection<Item, Filter> {
    const { isAllSelected, includedIds, excludedIds } = event;
    const { filter } = this.createLazyLoadMetadataFromCurrentTableState();

    return new TableRowsSelection(
      isAllSelected,
      includedIds,
      excludedIds,
      filter,
    );
  }

  private listenToTableLazyLoad(): void {
    this.tableLazyLoadTrigger$
      .pipe(
        throttleTime(50),
        tap((lazyLoad) => this.notifyTableLazyLoadEvent(lazyLoad)),
        switchMap((lazyLoad) => this.fetchByLazyLoadOnce(lazyLoad)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        this.updateTableDataOnUI(payload);
      });
  }

  private listenToTableRefresh(): void {
    this.table?.refresh$
      .pipe(
        switchMap(() =>
          this.tableLazyLoadTrigger$.pipe(
            throttleTime(50),
            switchMap((lazyLoad) => this.fetchByLazyLoadOnce(lazyLoad)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        this.notifyRefreshed();
        this.updateTableDataOnUI(payload);
      });
  }

  private fetchByLazyLoadOnce(
    lazyLoad: TableLazyLoad<Filter>,
  ): Observable<TableDataPayload<Item>> {
    return this.dataSource.fetch(lazyLoad).pipe(
      once(),
      catchError(() => of(new TableDataPayload([], 0, 0, 0))),
    );
  }

  private updateTableDataOnUI(payload: TableDataPayload<Item>): void {
    const { data, total, first, size } = payload;

    this.data.set(data);
    this.total.set(total);
    this.rows.set(valueOrDefault(size, DEFAULT_PAGE_SIZE));
    this.first.set(first);

    if (!this.isFirstLoaded) {
      this.markAsFirstLoaded();
    }

    this.dataLoadedSource$.next(this.data());

    this.detectChanges();
  }

  private notifyTableLazyLoadEvent(lazyLoad: TableLazyLoad<Filter>): void {
    this.tableEventsSource?.notifyLazyLoad(lazyLoad);
  }

  private prepareTableSort(
    lazyLoadEvent: TableLazyLoadEvent,
  ): Nillable<TableSort> {
    const { sortField, sortOrder } = lazyLoadEvent;

    if (isNil(sortField)) {
      return;
    }

    if (isNil(sortOrder)) {
      return;
    }

    if (isArray(sortField)) {
      return;
    }

    return new TableSort(sortField, sortOrder);
  }

  private prepareTableFilter(
    lazyLoadEvent: TableLazyLoadEvent,
  ): Nillable<TableFilter<Filter>> {
    const { filters } = lazyLoadEvent;

    if (isNil(filters)) {
      return;
    }

    return filters as TableFilter<Filter>;
  }

  private filterBy(
    key: CollectionFilterKey<Filter>,
    value: CollectionFilterValue,
  ): void {
    this.component().filter(value, String(key), 'custom');
  }

  private markAsFirstLoaded(): void {
    this.isFirstLoaded = true;
  }

  private clearAllSortsAndTriggerTableLazyLoad(): void {
    this.clearAllSorts();
    this.triggerTableLazyLoad(
      this.createLazyLoadMetadataFromCurrentTableState(),
    );
  }

  private clearAllSorts(): void {
    // PrimeNG bezpośrednio nie udostępnia metody do usuwania sortowania tabeli.
    // Brzydkie rzutowanie musi zostać w taki sposób zrealizowane ze względu na to,
    // że w projekcie wykorzystujemy strict type, czego z kolei nie robi PrimeNG.
    // Metoda została zaimplementowana na podstawie metody clear(), pochodzącej z komponentu Table.

    this.component().sortField = null as unknown as string;
    this.component().sortOrder = this.component().defaultSortOrder;
    this.component().multiSortMeta = [];
    this.component().onSort.emit(null as unknown as SortMeta);
    this.component().tableService.onSort(null as unknown as SortMeta);
  }

  private triggerTableLazyLoad(tableLazyLoad: TableLazyLoad<Filter>): void {
    this.tableLazyLoadTrigger$.next(tableLazyLoad);
  }

  private createLazyLoadMetadataFromCurrentTableState(): TableLazyLoad<Filter> {
    return this.createLazyLoadMetadata(
      this.component().createLazyLoadMetadata(),
    );
  }

  private haveSelectedRowsSource(): Observable<boolean> {
    return this.selectionSource$.pipe(
      map(({ isAllSelected, includedIds }) => {
        return isAllSelected || includedIds.length > 0;
      }),
    );
  }

  private selectionSource(): Observable<
    Nillable<TableRowsSelection<Item, Filter>>
  > {
    return this.haveSelectedRowsSource$.pipe(
      switchMap((selected) => {
        if (selected) {
          return this.selectionSource$.asObservable();
        }

        return of(null);
      }),
    );
  }

  private refreshedSource(): Observable<void> {
    return this.refreshedSource$.asObservable();
  }

  private notifyRefreshed(): void {
    this.refreshedSource$.next();
  }

  protected readonly isNil = isNil;
}
